import localhost from "apis/localhost";
import {
  JSON_CONTENT_TYPE,
  MULTIPART_FORM_DATA_CONTENT_TYPE,
  URLENCODED_FORM_CONTENT_TYPE,
} from "constants/app";
import { showErrorModal } from "slices/app/actions/error";
import { pageLoading } from "slices/app/appSlice";
import {
  setDocumentInfo,
  setFolderInfo,
  setNavigationInfo,
} from "slices/workspace/workspaceSlice";

import { isTargetNode } from "../navigationNodes";
import { selectCurrentDocumentInfo } from "../selectors";

const prepareNavigationInfo = (
  parentId,
  stack,
  expandedTarget,
  nodes,
  iterator,
  expandedNodes,
  nodeMaps
) => {
  const resultData = [];
  nodes.forEach((oldNode) => {
    const node = { ...oldNode };
    stack.push(node);
    node.id = "" + iterator.index;
    node.parentId = parentId;
    iterator.index++;
    if (oldNode.children) {
      node.children = prepareNavigationInfo(
        node.vtid,
        stack,
        expandedTarget,
        oldNode.children,
        iterator,
        expandedNodes,
        nodeMaps
      );
    }
    if (isTargetNode(node, expandedTarget)) {
      stack.forEach((n) => expandedNodes.push(n.id));
    }
    resultData.push(node);
    stack.pop();
    const nodeMap = node.isDoc ? nodeMaps.documentsById : nodeMaps.foldersById;
    nodeMap[node.vtid] = node;
  });
  return resultData;
};

export const getNavigationInfo =
  (expandedTarget, handleSuccess, handleFailure) => async (dispatch) => {
    try {
      const res = await localhost.get("/folders/navigation");

      const expandedNodes = [];
      const foldersById = {};
      const documentsById = {};

      const navigationInfo = {
        tree: {
          id: "0",
          children: [
            {
              text: "Folders",
              id: "1",
              parentId: "0",
              isFolder: true,
              children: prepareNavigationInfo(
                1,
                [],
                expandedTarget,
                res.data,
                { index: 2 },
                expandedNodes,
                { foldersById, documentsById }
              ),
            },
          ],
        },
        expandedNodes,
        foldersById,
        documentsById,
      };

      dispatch(setNavigationInfo(navigationInfo));

      handleSuccess?.(navigationInfo);
    } catch (error) {
      console.error(error);
      handleFailure?.(error);
    }
  };

export const getFolderInfo = (folderIdParam) => async (dispatch, getState) => {
  const state = getState();
  const currentDocument = selectCurrentDocumentInfo(state);
  const hasFolderChanged = currentDocument?.folderId !== folderIdParam;

  dispatch(pageLoading(true));
  try {
    const rsp = await localhost.get(`/folders/${folderIdParam}`);
    dispatch(setFolderInfo(rsp.data));

    if (hasFolderChanged) {
      dispatch(setDocumentInfo(undefined));
    }
    return rsp.data;
  } catch (err) {
    return null;
  } finally {
    dispatch(pageLoading(false));
  }
};

export const getDocumentInfo =
  (folderIdParam, documentIdParam, handleFailure, handleSuccess) =>
  async (dispatch) => {
    dispatch(pageLoading(true));
    try {
      const rsp = await localhost.get(
        `/folders/${folderIdParam}/${documentIdParam}`
      );
      dispatch(setDocumentInfo(rsp.data));
      handleSuccess?.(rsp.data);
      return rsp.data;
    } catch (err) {
      handleFailure?.(err);
      return null;
    } finally {
      dispatch(pageLoading(false));
    }
  };

export const getAcronymReport =
  (folderId, documentId, handleSuccess) => async (dispatch) => {
    dispatch(pageLoading(true));
    await localhost
      .get("/acronyms", {
        params: {
          folderId,
          documentId,
        },
      })
      .then(async (response) => {
        const { data } = response;
        handleSuccess(data);
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(pageLoading(false));
      });
  };

export const getWorkspaceLink = ({ folderId, documentId }) => {
  let link = "/workspace";
  if (folderId !== undefined) {
    link += `/${folderId}`;
  }
  if (documentId !== undefined) {
    link += `/${documentId}`;
  }
  return link;
};

export const compareDocuments = (args) =>
  localhost.post("/compare/export", args, {
    headers: {
      "Content-Type": JSON_CONTENT_TYPE,
    },
  });

export const statusCompareDocuments = (jobId) =>
  localhost.get(`/jobStatus?jobId=${jobId}`);

export const acronymExport = (args) =>
  localhost.post("/report", args, {
    headers: {
      "Content-Type": URLENCODED_FORM_CONTENT_TYPE,
    },
  });

export const statusAcronymExport = (jobId) =>
  localhost.get(`/jobStatus?jobId=${jobId}`);

export const moveDocumentPosition = (args) =>
  localhost.post("/folders/moveDocumentPosition", args, {
    headers: {
      "Content-Type": JSON_CONTENT_TYPE,
    },
  });

export const moveDocumentToFolder = ({ folderId, destFolderId, documentId }) =>
  localhost.put(
    `/folders/${folderId}/move/${documentId}/to/${destFolderId}`,
    null,
    {
      headers: {
        "Content-Type": JSON_CONTENT_TYPE,
      },
    }
  );

export const moveDocumentBeforeDocument = ({
  destFolderId,
  destDocumentId,
  documentId,
  folderId,
}) =>
  localhost.put(
    `/folders/${destFolderId}/before/${destDocumentId}/move/${documentId}/from/${folderId}`,
    null,
    {
      headers: {
        "Content-Type": JSON_CONTENT_TYPE,
      },
    }
  );

export const moveDocumentsToFolder = ({
  folderId,
  destFolderId,
  documentIds,
}) => localhost.put(`/folders/${folderId}/to/${destFolderId}`, documentIds);

export const removeFolder = ({ folderId }) =>
  localhost.delete(`/folders/${folderId}`, null, {
    headers: {
      "Content-Type": JSON_CONTENT_TYPE,
    },
  });

export const removeDocument = ({ folderId, documentId }) =>
  localhost.delete(`/folders/${folderId}/${documentId}`, null, {
    headers: {
      "Content-Type": JSON_CONTENT_TYPE,
    },
  });

export const removeMultipleDocuments = ({ folderId, documentIds }) =>
  localhost.post(`/folders/${folderId}/removeDocuments`, documentIds);

export const renameFolder = ({ folderId, folderName }) =>
  localhost.post(
    `/folders/${folderId}/rename`,
    { folderName },
    {
      headers: {
        "Content-Type": MULTIPART_FORM_DATA_CONTENT_TYPE,
      },
    }
  );

export const moveFolder = ({ folderId, destFolderId }) =>
  localhost.post(`/folders/${folderId}/move/${destFolderId}`, null);

export const downloadFolder = ({ folderId }) =>
  localhost.post(`/folders/downloadFolder?folderId=${folderId}`);

export const reanalyseFolder = ({ folderId }) =>
  localhost.post(`/folders/${folderId}/reanalyse`);

export const getReanalysingFiles = (handleSuccess) => async (dispatch) => {
  await localhost
    .get("/folders/reanalysing")
    .then((res) => {
      handleSuccess(res.data);
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    });
};
